import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { fetchErrorDetail } from "helpers";
import { useBasketMaterials } from "hooks/useMaterials";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useTenantData } from "contexts/tenant";
import { UIEvent, useEffect, useState } from "react";
import {
  generatedApi,
  HeatQueueEntry,
  useCreateHeatOverrideMutation,
  useListHeatOverrideReasonsQuery,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import {
  OverrideReasonFields,
  subreasonOptionsFor,
} from "../override/OverrideReasonFields";
import { DeltaAdjustmentStep } from "./DeltaAdjustmentStep";
import {
  materialDeltaHint,
  MaterialDeltaRow,
  resolveMaterialDeltas,
} from "./materialDeltaRows";
import { useDeltaWeightFormat } from "./useDeltaWeightFormat";

type Step = "adjustments" | "reason";

const hasContentBelowViewport = (element: HTMLElement) =>
  element.scrollTop + element.clientHeight < element.scrollHeight - 1;

const useHasContentBelow = () => {
  // Use state, not refs: the dialog portal mounts its content after the first effect pass, and only state changes re-run the effect.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null
  );
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null
  );
  const [hasContentBelow, setHasContentBelow] = useState(false);
  useEffect(() => {
    if (scrollElement === null || contentElement === null) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setHasContentBelow(hasContentBelowViewport(scrollElement));
    });
    observer.observe(scrollElement);
    observer.observe(contentElement);
    return () => {
      observer.disconnect();
    };
  }, [scrollElement, contentElement]);
  return {
    setScrollElement,
    setContentElement,
    hasContentBelow,
    onScroll: (event: UIEvent<HTMLDivElement>) => {
      setHasContentBelow(hasContentBelowViewport(event.currentTarget));
    },
  };
};

interface Props {
  heats: HeatQueueEntry[];
  onClose: () => void;
  onOverrideCreated: () => void;
}

export const DeltaOverrideDialog = ({
  heats,
  onClose,
  onOverrideCreated,
}: Props) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { heat_override_options: heatOverrideOptions } = useTenantData();
  const commentRequired = heatOverrideOptions.includes("comment_required");
  const dispatch = useAppDispatch();
  const { massScaleFactor } = useDeltaWeightFormat();
  const basketMaterials = useBasketMaterials();
  const reasons = useListHeatOverrideReasonsQuery({ tenantName });
  const reasonOptions = reasons.data?.reasons ?? [];
  const [createOverride, createState] = useCreateHeatOverrideMutation();

  const [step, setStep] = useState<Step>("adjustments");
  const [rowsByBasketNumber, setRowsByBasketNumber] = useState<
    Map<number, MaterialDeltaRow[]>
  >(() => new Map());
  // A queue refresh can change the selection, so the entered changes keep the heats from when the dialog opened.
  const [chosenHeats] = useState(heats);
  const [reason, setReason] = useState<string | null>(null);
  const [subreason, setSubreason] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { setScrollElement, setContentElement, hasContentBelow, onScroll } =
    useHasContentBelow();

  const floorMix = chosenHeats[0]?.floor_recipe?.mix ?? null;
  const baskets = [...(floorMix?.baskets ?? [])].sort(
    (left, right) => left.basket_number - right.basket_number
  );

  const materialResults = baskets.flatMap((basket) => {
    const rows = rowsByBasketNumber.get(basket.basket_number) ?? [];
    return rows.length === 0
      ? []
      : [
          {
            basketNumber: basket.basket_number,
            result: resolveMaterialDeltas({ rows, basket, massScaleFactor }),
          },
        ];
  });
  const chargeDeltas = materialResults.flatMap(({ basketNumber, result }) =>
    result.kind === "valid"
      ? [
          {
            basket_number: basketNumber,
            material_deltas: result.materialDeltas,
          },
        ]
      : []
  );
  const firstChargeProblem = materialResults.flatMap(({ result }) =>
    result.kind === "invalid" ? [result.problem] : []
  )[0];
  const requiresSubreason =
    subreasonOptionsFor({ reasonOptions, reason }).length > 0;

  const hint = ((): string | null => {
    switch (step) {
      case "adjustments":
        if (firstChargeProblem !== undefined) {
          return materialDeltaHint({ problem: firstChargeProblem, t });
        }
        return chargeDeltas.length > 0
          ? null
          : t("deltaAdjustmentRequiredHint");
      case "reason":
        if (reason === null) {
          return t("deltaReasonRequiredHint");
        }
        if (requiresSubreason && subreason === null) {
          return t("deltaSubreasonRequiredHint");
        }
        return commentRequired && comment.trim().length === 0
          ? t("overrideCommentRequiredHint")
          : null;
    }
  })();

  const heatScope = (() => {
    const first = chosenHeats[0];
    const last = chosenHeats.at(-1);
    if (first === undefined || last === undefined) {
      return null;
    }
    return first === last
      ? t("overrideAppliesToHeat", { heatId: first.heat_id })
      : t("overrideAppliesToHeats", {
          heats: `${String(first.heat_id)} – ${String(last.heat_id)}`,
        });
  })();

  const title = (() => {
    switch (step) {
      case "adjustments":
        return t("adjustMaterials");
      case "reason":
        return t("deltaReasonTitle");
    }
  })();

  const handleSave = async () => {
    if (floorMix === null || reason === null) {
      return;
    }
    setSubmitError(null);
    const result = await createOverride({
      tenantName,
      create: {
        mode: "delta",
        stable_heat_ids: chosenHeats.map((heat) => heat.stable_heat_id),
        reason,
        subreason: requiresSubreason ? subreason : null,
        comment: comment.trim() === "" ? null : comment.trim(),
        baseline_mix_id: floorMix.id,
        charge_deltas: chargeDeltas,
      },
    });
    if ("data" in result) {
      dispatch(generatedApi.util.invalidateTags(["deployments", "heat_queue"]));
      onOverrideCreated();
    } else {
      setSubmitError(fetchErrorDetail(result.error) ?? t("deltaSaveError"));
    }
  };

  const continueAction = ((): (() => void) | null => {
    switch (step) {
      case "adjustments":
        return () => {
          setStep("reason");
        };
      case "reason":
        return null;
    }
  })();

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={step === "adjustments" ? "lg" : "md"}
      fullWidth
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        {title}
        {heatScope !== null && (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {heatScope}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent
        ref={setScrollElement}
        onScroll={onScroll}
        dividers={step === "adjustments"}
        sx={{ mt: 0, mb: 0, pb: 2, borderColor: "grey.300" }}
      >
        <Box ref={setContentElement}>
          {step === "adjustments" && (
            <DeltaAdjustmentStep
              baskets={baskets}
              adjustedBasketNumbers={
                new Set(chargeDeltas.map((charge) => charge.basket_number))
              }
              rowsByBasketNumber={rowsByBasketNumber}
              onRowsChange={({ basketNumber, rows }) => {
                setRowsByBasketNumber((current) =>
                  new Map(current).set(basketNumber, rows)
                );
              }}
              materials={basketMaterials}
            />
          )}
          {step === "reason" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              <OverrideReasonFields
                reasonOptions={reasonOptions}
                reason={reason}
                subreason={subreason}
                comment={comment}
                commentRequired={commentRequired}
                onReasonChange={(selectedReason) => {
                  setReason(selectedReason);
                  setSubreason(null);
                }}
                onSubreasonChange={setSubreason}
                onCommentChange={setComment}
              />
            </Box>
          )}
          {submitError !== null && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ position: "relative", gap: 1, py: 1.5 }}>
        <Button
          onClick={() => {
            switch (step) {
              case "adjustments":
                onClose();
                return;
              case "reason":
                setStep("adjustments");
                return;
            }
          }}
        >
          {step === "adjustments" ? t("cancel") : t("back")}
        </Button>
        <Box sx={{ flex: 1 }} />
        {hint !== null && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {hint}
          </Typography>
        )}
        {continueAction !== null && (
          <Button
            variant="contained"
            disabled={hint !== null}
            onClick={continueAction}
          >
            {t("continue")}
          </Button>
        )}
        {step === "reason" && (
          <LoadingButton
            variant="contained"
            loading={createState.isLoading}
            disabled={hint !== null}
            onClick={() => {
              void handleSave();
            }}
          >
            {t("save")}
          </LoadingButton>
        )}
        {hasContentBelow && (
          <Box
            sx={(theme) => ({
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "calc(100% + 1px)",
              height: 40,
              pointerEvents: "none",
              background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
            })}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
